import dayjs from 'dayjs';
import type { ProductStatusFilterValue } from 'app/shared/components/productStatusFilterUtils';
import { matchesProductStatusFilter } from 'app/shared/components/productStatusFilterUtils';
import type { TaxManagerState } from './reducer';
import type { TaxRuleListRow } from 'app/shared/api/taxRules/types';
import type { CentreListItem } from 'app/shared/api/getCentres';
import { parsePositiveCentreId, positiveNumericCentreId } from 'app/shared/api/getCentres';

const root = (s: object): TaxManagerState => (s as { taxManager?: TaxManagerState }).taxManager ?? ({} as TaxManagerState);

export const getTaxLoading = (s: object): boolean => root(s).loading ?? false;
export const getTaxList = (s: object): TaxRuleListRow[] => root(s).list ?? [];
export const getTaxFilterName = (s: object): string => root(s).filterName ?? '';
export const getTaxFilterCentre = (s: object) => root(s).filterCentre ?? null;
export const getTaxFilterCountry = (s: object) => root(s).filterCountry ?? null;
export const getTaxFilterSpecificCentre = (s: object): boolean => root(s).filterSpecificCentre ?? false;
export const getTaxFilterStartDateFrom = (s: object): string | null => root(s).filterStartDateFrom ?? null;

function findCentreListItemById(centres: CentreListItem[], rid: number): CentreListItem | undefined {
  for (const x of centres) {
    const pid = positiveNumericCentreId(x);
    if (pid != null && pid === rid) return x;
    const idNum = Number(String(x.id).trim());
    if (Number.isFinite(idNum) && Math.trunc(idNum) === rid) return x;
  }
  return undefined;
}

function rowMatchesTaxCountryFilter(
  row: TaxRuleListRow,
  fc: { countryId?: number; countryCode?: string },
  centres: CentreListItem[] | undefined
): boolean {
  const cid = parsePositiveCentreId(row.centreId);
  if (cid != null && centres?.length) {
    const c = findCentreListItemById(centres, cid);
    if (c) {
      if (fc.countryId != null && c.countryId != null) {
        return Number(fc.countryId) === Number(c.countryId);
      }
      if (fc.countryCode != null && c.countryCode != null) {
        return (
          String(fc.countryCode).trim().toUpperCase() ===
          String(c.countryCode).trim().toUpperCase()
        );
      }
    }
  }
  if (row.countryId == null) return false;
  if (typeof row.countryId === 'number' && fc.countryId != null) {
    return Number(row.countryId) === Number(fc.countryId);
  }
  const code = String(row.countryId).trim().toUpperCase();
  if (fc.countryCode != null && code) {
    return code === String(fc.countryCode).trim().toUpperCase();
  }
  return false;
}

export type TaxFilteredRowsOpts = {
  centres?: CentreListItem[];
  activeStatusFilter?: ProductStatusFilterValue;
};

export function getFilteredTaxRows(state: object, opts: TaxFilteredRowsOpts = {}): TaxRuleListRow[] {
  const st = root(state);
  const nameQ = (st.filterName ?? '').trim().toLowerCase();
  const activeStatusFilter = opts.activeStatusFilter ?? 'all';
  const fc = st.filterCentre;
  const fCountry = st.filterCountry;
  const specific = st.filterSpecificCentre ?? false;
  const startFrom = st.filterStartDateFrom ? dayjs(st.filterStartDateFrom) : null;
  const centres = opts.centres;

  return (st.list ?? []).filter((row) => {
    if (!matchesProductStatusFilter(row.isActive, activeStatusFilter)) return false;
    if (nameQ && !row.name.toLowerCase().includes(nameQ)) return false;

    if (specific) {
      let fcIdNum = NaN;
      if (fc?.id != null && fc.id !== '') {
        if (typeof fc.id === 'number' && Number.isFinite(fc.id) && fc.id > 0) {
          fcIdNum = Math.trunc(fc.id);
        } else {
          const parsed = Number(String(fc.id).trim());
          if (Number.isFinite(parsed) && parsed > 0) fcIdNum = Math.trunc(parsed);
        }
      }
      if (Number.isFinite(fcIdNum) && fcIdNum > 0) {
        const matchesCentre = row.centreId != null && Number(row.centreId) === fcIdNum;
        if (!matchesCentre) return false;
      }
    } else if (fCountry && (fCountry.countryId != null || fCountry.countryCode != null)) {
      if (!rowMatchesTaxCountryFilter(row, fCountry, centres)) return false;
    }

    if (startFrom && startFrom.isValid() && row.startDate && row.startDate.isValid()) {
      if (row.startDate.isBefore(startFrom, 'day')) return false;
    }

    return true;
  });
}